import { Box, FlexBox, Text, Tooltip } from '@dixa/lib-shared-habla';
import { colors, typography } from '@dixa/lib-shared-tokens';
import { formatInstrumentNumber, MetricInstrumentData } from 'lib-analytics-ui';
import { DashboardSLAPolicyMetric } from 'lib-dashboard-types';
import { useHasAccess } from 'lib-shared-access-control';
import { memo } from 'react';

import { AppLink } from 'components/appLink';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';

type DashboardSLAResponseTimeMetricProps = {
  /** The average response time. */
  responseTime?: number | null;
  /** The SLA policy ID. */
  slaPolicyId: number;
  /** The SLA policy metric. */
  slaPolicyMetric?: DashboardSLAPolicyMetric;
};

export const DashboardSLAResponseTimeMetric = memo(
  function DashboardSLAResponseTimeMetric({
    responseTime,
    slaPolicyId,
    slaPolicyMetric,
  }: DashboardSLAResponseTimeMetricProps): JSX.Element {
    const flags = useFeatureFlag(['sla-management']);
    const hasSLAAccess = useHasAccess('settings.sla');

    const { percentage: goalPercentage = 0, target: targetResponseTime = 0 } =
      slaPolicyMetric ?? {};

    const renderPolicyGoal = (): JSX.Element => {
      const goalText = (
        <Text.Paragraph variant="3" color="tertiary" as="span">
          Goal {goalPercentage}% in{' '}
          {formatInstrumentNumber(targetResponseTime, 'duration')}
        </Text.Paragraph>
      );

      if (flags.slaManagement && hasSLAAccess) {
        return (
          <Tooltip content="Go to SLA policy settings">
            <AppLink
              path="/settings/sla/policies/:id"
              params={{
                path: { id: `${slaPolicyId}` },
              }}
            >
              {goalText}
            </AppLink>
          </Tooltip>
        );
      }

      return goalText;
    };

    return (
      <FlexBox flexDirection="column" gap="200">
        <Text.Paragraph variant="4" subdued>
          Avg. response time:{' '}
          <Text.Paragraph
            variant="3"
            as="span"
            color={!responseTime ? typography.color.tertiary : undefined}
          >
            {responseTime
              ? formatInstrumentNumber(responseTime, 'duration')
              : 'n.a.'}
          </Text.Paragraph>
        </Text.Paragraph>

        <Box alignSelf="center">
          <MetricInstrumentData
            data={slaPolicyMetric?.achieved_percentage ?? null}
            format="percentage"
          />
        </Box>

        <FlexBox flexDirection="column" alignItems="flex-end">
          <svg width="100%" height="18px">
            <text
              x="0%"
              y="50%"
              dy="4"
              fontSize={typography.size[200]}
              fill={typography.color.tertiary}
            >
              0
            </text>

            <svg x="6%" width="75%" style={{ overflow: 'visible' }}>
              <line
                x1="0%"
                x2="100%"
                y1="50%"
                y2="50%"
                stroke={colors.neutral[400]}
                strokeWidth="1"
                strokeDasharray="3,2"
              />

              <line
                x1={`${goalPercentage}%`}
                x2={`${goalPercentage}%`}
                y1="30%"
                y2="100%"
                stroke={colors.neutral[400]}
              />

              {slaPolicyMetric?.achieved_percentage && (
                <circle
                  cx={`${slaPolicyMetric.achieved_percentage}%`}
                  cy="50%"
                  r="3"
                  fill={colors.neutral['000']}
                  stroke={
                    slaPolicyMetric.achieved_percentage >= goalPercentage
                      ? colors.green[500]
                      : colors.orange[600]
                  }
                  strokeWidth="1"
                />
              )}
            </svg>

            <text
              x="100%"
              y="50%"
              dy="4"
              fontSize={typography.size[200]}
              textAnchor="end"
              fill={typography.color.tertiary}
            >
              100%
            </text>
          </svg>

          {renderPolicyGoal()}
        </FlexBox>
      </FlexBox>
    );
  },
);
